<template>
  <div class="pedido-ver" style="padding:1.25rem;color:var(--text-primary)">

    <!-- Cabecera -->
    <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
      <h5 class="mb-0 fw-semibold" style="color:var(--text-primary)">
        <i class="bi bi-bag-check me-2" style="color:var(--primary-main)"></i>
        Pedido {{ pedido ? pedido.numero_pedido : '...' }}
      </h5>
      <div class="d-flex gap-2 flex-wrap">
        <button
          v-if="pedido && pedido.estado === 'pendiente'"
          class="btn btn-sm btn-outline-secondary"
          @click="$router.push('/ventas/pedidos/' + pedido.id + '/editar')"
        >
          <i class="bi bi-pencil me-1"></i>Editar
        </button>
        <button
          v-if="pedido && pedido.estado !== 'cancelado' && pedido.estado !== 'facturado' && pedido.estado !== 'pagado'"
          class="btn btn-sm btn-outline-primary"
          @click="abrirModalEstado"
        >
          <i class="bi bi-arrow-left-right me-1"></i>Cambiar estado
        </button>
        <button
          v-if="pedido && pedido.estado !== 'cancelado' && pedido.estado !== 'facturado'"
          class="btn btn-sm btn-success"
          @click="abrirModalConversion"
        >
          <i class="bi bi-receipt-cutoff me-1"></i>Convertir a Factura
        </button>
        <button class="btn btn-sm btn-outline-secondary" @click="$router.push('/ventas/pedidos')">
          <i class="bi bi-arrow-left me-1"></i>Volver
        </button>
      </div>
    </div>

    <!-- Cargando -->
    <div v-if="cargando" class="text-center py-5" style="color:var(--text-muted)">
      <div class="spinner-border spinner-border-sm me-2"></div>Cargando pedido...
    </div>

    <!-- Error -->
    <div v-else-if="errorMensaje" class="alert alert-danger d-flex align-items-center gap-2 py-2">
      <i class="bi bi-exclamation-triangle-fill"></i>{{ errorMensaje }}
    </div>

    <template v-else-if="pedido">

      <!-- Info del pedido -->
      <div class="card shadow-sm mb-3" style="background:var(--secondary-bg-main);border:1px solid var(--border-main)">
        <div class="card-header py-2 px-3 fw-semibold small"
             style="background:var(--primary-main);color:#fff;border-radius:.375rem .375rem 0 0">
          <i class="bi bi-info-circle me-1"></i>Información del Pedido
        </div>
        <div class="card-body py-3 px-3">
          <div class="row g-3 small">
            <div class="col-6 col-md-3">
              <div style="color:var(--text-secondary)">N° Pedido</div>
              <div class="fw-semibold">{{ pedido.numero_pedido }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div style="color:var(--text-secondary)">Estado</div>
              <span class="badge" :class="badgeEstado(pedido.estado)">{{ labelEstado(pedido.estado) }}</span>
            </div>
            <div class="col-6 col-md-3">
              <div style="color:var(--text-secondary)">Origen</div>
              <span class="badge" :class="pedido.origen === 'app_movil' ? 'bg-info text-dark' : 'bg-secondary'">
                <i :class="pedido.origen === 'app_movil' ? 'bi bi-phone' : 'bi bi-display'" class="me-1"></i>
                {{ pedido.origen === 'app_movil' ? 'App Móvil' : 'Web' }}
              </span>
            </div>
            <div class="col-6 col-md-3">
              <div style="color:var(--text-secondary)">Fecha pedido</div>
              <div>{{ formatearFecha(pedido.created_at) }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div style="color:var(--text-secondary)">Cliente</div>
              <div class="fw-semibold">{{ pedido.cliente_nombre }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div style="color:var(--text-secondary)">Identificación</div>
              <div>{{ pedido.cliente_codigo || '—' }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div style="color:var(--text-secondary)">Vendedor</div>
              <div>{{ pedido.vendedor_nombre || '—' }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div style="color:var(--text-secondary)">Entrega estimada</div>
              <div>{{ pedido.fecha_estimada_entrega ? formatearFecha(pedido.fecha_estimada_entrega) : '—' }}</div>
            </div>
            <div v-if="pedido.observaciones" class="col-12">
              <div style="color:var(--text-secondary)">Notas</div>
              <div>{{ pedido.observaciones }}</div>
            </div>
            <div v-if="pedido.motivo_cancelacion" class="col-12">
              <div style="color:var(--text-secondary)">Motivo cancelación</div>
              <div class="text-danger">{{ pedido.motivo_cancelacion }}</div>
            </div>
            <div v-if="pedido.factura_id" class="col-12">
              <div style="color:var(--text-secondary)">Factura generada</div>
              <span class="badge bg-success"><i class="bi bi-receipt-cutoff me-1"></i>Factura #{{ pedido.factura_id }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Detalle de productos -->
      <div class="card shadow-sm mb-3" style="background:var(--secondary-bg-main);border:1px solid var(--border-main)">
        <div class="card-header py-2 px-3 fw-semibold small"
             style="background:var(--primary-main);color:#fff;border-radius:.375rem .375rem 0 0">
          <i class="bi bi-box-seam me-1"></i>Productos / Servicios
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-sm mb-0" style="font-size:.82rem">
              <thead style="background:var(--bg-main)">
                <tr style="color:var(--text-secondary)">
                  <th class="ps-3">Descripción</th>
                  <th class="text-center">Cant.</th>
                  <th class="text-end">P. Unit.</th>
                  <th class="text-end">Desc.</th>
                  <th class="text-center">IVA %</th>
                  <th class="text-end pe-3">Total</th>
                </tr>
              </thead>
              <tbody style="color:var(--text-primary)">
                <tr v-for="d in pedido.items" :key="d.id">
                  <td class="ps-3">
                    <div>{{ d.producto_nombre }}</div>
                    <div v-if="d.producto_codigo" class="text-muted" style="font-size:.75rem">{{ d.producto_codigo }}</div>
                  </td>
                  <td class="text-center">{{ d.cantidad }}</td>
                  <td class="text-end">$ {{ parseFloat(d.precio_unitario || 0).toFixed(2) }}</td>
                  <td class="text-end">$ {{ parseFloat(d.descuento || 0).toFixed(2) }}</td>
                  <td class="text-center">{{ parseFloat(d.porcentaje_iva || 0).toFixed(0) }}%</td>
                  <td class="text-end pe-3 fw-semibold" style="color:var(--accent-success)">
                    $ {{ parseFloat(d.total_detalle || 0).toFixed(2) }}
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr style="color:var(--text-secondary);font-size:.78rem">
                  <td colspan="5" class="text-end ps-3">Subtotal:</td>
                  <td class="text-end pe-3">$ {{ parseFloat(pedido.subtotal || 0).toFixed(2) }}</td>
                </tr>
                <tr style="color:var(--text-secondary);font-size:.78rem">
                  <td colspan="5" class="text-end ps-3">IVA:</td>
                  <td class="text-end pe-3">$ {{ parseFloat(pedido.iva || 0).toFixed(2) }}</td>
                </tr>
                <tr class="fw-bold border-top" style="color:var(--text-primary);font-size:.9rem">
                  <td colspan="5" class="text-end ps-3">TOTAL:</td>
                  <td class="text-end pe-3" style="color:var(--accent-success)">
                    $ {{ parseFloat(pedido.total || 0).toFixed(2) }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

    </template>

    <!-- ── Modal: Cambiar estado ── -->
    <div v-if="modalEstado.visible" class="modal-overlay" @click.self="modalEstado.visible = false">
      <div class="modal-caja" style="max-width:420px">
        <div class="modal-header-caja d-flex align-items-center justify-content-between">
          <span class="fw-semibold" style="color:var(--text-primary)">
            <i class="bi bi-arrow-left-right me-2" style="color:var(--primary-main)"></i>Cambiar Estado
          </span>
          <button class="btn-close-caja" @click="modalEstado.visible = false">✕</button>
        </div>
        <div class="modal-body-caja">
          <p class="small mb-2" style="color:var(--text-secondary)">
            Pedido: <strong style="color:var(--text-primary)">{{ pedido && pedido.numero_pedido }}</strong>
          </p>
          <div class="mb-3">
            <label class="form-label small" style="color:var(--text-secondary)">Nuevo estado</label>
            <select
              v-model="modalEstado.nuevoEstado"
              class="form-select form-select-sm"
              style="background:var(--bg-main);color:var(--text-primary);border-color:var(--border-main)"
            >
              <option value="pendiente">Pendiente</option>
              <option value="confirmado">Confirmado</option>
              <option value="en_preparacion">En preparación</option>
              <option value="listo">Listo</option>
              <option value="entregado">Entregado</option>
              <option value="pagado">Pagado</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>
          <div v-if="modalEstado.nuevoEstado === 'cancelado'" class="mb-3">
            <label class="form-label small" style="color:var(--text-secondary)">Motivo de cancelación</label>
            <input
              type="text"
              v-model="modalEstado.motivo"
              class="form-control form-control-sm"
              placeholder="Opcional"
              style="background:var(--bg-main);color:var(--text-primary);border-color:var(--border-main)"
            />
          </div>
        </div>
        <div class="modal-footer-caja d-flex gap-2 justify-content-end">
          <button class="btn btn-outline-secondary btn-sm" @click="modalEstado.visible = false">Cancelar</button>
          <button class="btn btn-primary btn-sm" @click="ejecutarCambioEstado" :disabled="modalEstado.guardando">
            <span v-if="modalEstado.guardando" class="spinner-border spinner-border-sm me-1"></span>
            Guardar
          </button>
        </div>
      </div>
    </div>

    <!-- ── Modal: Convertir a factura ── -->
    <div v-if="modalConversion.visible" class="modal-overlay" @click.self="modalConversion.visible = false">
      <div class="modal-caja" style="max-width:440px">
        <div class="modal-header-caja d-flex align-items-center justify-content-between">
          <span class="fw-semibold" style="color:var(--text-primary)">
            <i class="bi bi-receipt-cutoff me-2" style="color:var(--accent-success)"></i>Convertir a Factura
          </span>
          <button class="btn-close-caja" @click="modalConversion.visible = false">✕</button>
        </div>
        <div class="modal-body-caja">
          <div class="alert alert-info py-2 small">
            <i class="bi bi-info-circle me-1"></i>
            Se pre-cargarán los datos del pedido <strong>{{ pedido && pedido.numero_pedido }}</strong>
            en el formulario de facturación. La factura se asignará al vendedor original del pedido.
          </div>
          <div v-if="modalConversion.cargando" class="text-center py-3" style="color:var(--text-muted)">
            <div class="spinner-border spinner-border-sm me-2"></div>Preparando datos...
          </div>
          <div v-if="modalConversion.error" class="alert alert-danger py-2 small">{{ modalConversion.error }}</div>
        </div>
        <div class="modal-footer-caja d-flex gap-2 justify-content-end">
          <button class="btn btn-outline-secondary btn-sm" @click="modalConversion.visible = false" :disabled="modalConversion.cargando">Cancelar</button>
          <button class="btn btn-success btn-sm" @click="ejecutarConversion" :disabled="modalConversion.cargando">
            <span v-if="modalConversion.cargando" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-arrow-right-circle me-1"></i>Ir a Facturación
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { pedidosService } from '@/services/pedidosService'
import notificaciones from '@/services/notificationService.js'

export default {
  name: 'ClientVentasPedidoVer',

  data() {
    return {
      cargando:     true,
      pedido:       null,
      errorMensaje: '',

      modalEstado: {
        visible:     false,
        nuevoEstado: '',
        motivo:      '',
        guardando:   false
      },

      modalConversion: {
        visible:  false,
        cargando: false,
        error:    ''
      }
    }
  },

  mounted() {
    this.cargarPedido()
  },

  methods: {
    async cargarPedido() {
      this.cargando     = true
      this.errorMensaje = ''
      try {
        const res  = await pedidosService.obtener(this.$route.params.id)
        this.pedido = res.data.data
      } catch {
        this.errorMensaje = 'No se pudo cargar el pedido.'
      } finally {
        this.cargando = false
      }
    },

    // ── Modal estado ────────────────────────────────────────
    abrirModalEstado() {
      this.modalEstado.nuevoEstado = this.pedido.estado
      this.modalEstado.motivo      = ''
      this.modalEstado.guardando   = false
      this.modalEstado.visible     = true
    },

    async ejecutarCambioEstado() {
      this.modalEstado.guardando = true
      try {
        await pedidosService.cambiarEstado(
          this.pedido.id,
          this.modalEstado.nuevoEstado,
          this.modalEstado.motivo || null
        )
        notificaciones.success('Estado actualizado a "' + this.labelEstado(this.modalEstado.nuevoEstado) + '".')
        this.modalEstado.visible = false
        await this.cargarPedido()
      } catch (e) {
        notificaciones.error(e?.response?.data?.error || 'Error al cambiar el estado.')
      } finally {
        this.modalEstado.guardando = false
      }
    },

    // ── Modal conversión ────────────────────────────────────
    abrirModalConversion() {
      this.modalConversion.cargando = false
      this.modalConversion.error    = ''
      this.modalConversion.visible  = true
    },

    async ejecutarConversion() {
      this.modalConversion.cargando = true
      this.modalConversion.error    = ''
      try {
        const res   = await pedidosService.convertir(this.pedido.id)
        const datos = res.data.data
        this.modalConversion.visible = false
        this.$router.push({
          path:  '/ventas/facturacion',
          query: { pedido_id: this.pedido.id },
          state: { pedidoPreCargado: datos }
        })
      } catch (e) {
        this.modalConversion.error = e?.response?.data?.error || 'Error al obtener los datos del pedido.'
      } finally {
        this.modalConversion.cargando = false
      }
    },

    // ── Helpers ─────────────────────────────────────────────
    formatearFecha(fecha) {
      if (!fecha) return '—'
      return new Date(fecha).toLocaleDateString('es-EC', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      })
    },

    labelEstado(estado) {
      const mapa = {
        pendiente:      'Pendiente',
        confirmado:     'Confirmado',
        en_preparacion: 'En preparación',
        listo:          'Listo',
        entregado:      'Entregado',
        pagado:         'Pagado',
        cancelado:      'Cancelado',
        facturado:      'Facturado'
      }
      return mapa[estado] || estado
    },

    badgeEstado(estado) {
      const mapa = {
        pendiente:      'bg-secondary',
        confirmado:     'bg-primary',
        en_preparacion: 'bg-warning text-dark',
        listo:          'bg-info text-dark',
        entregado:      'bg-success',
        pagado:         'bg-success',
        cancelado:      'bg-danger',
        facturado:      'bg-success'
      }
      return mapa[estado] || 'bg-secondary'
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.modal-caja {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: .5rem;
  width: 100%;
  box-shadow: var(--shadow-md);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
}

.modal-header-caja {
  padding: .75rem 1rem;
  border-bottom: 1px solid var(--border-main);
  flex-shrink: 0;
}

.modal-body-caja {
  padding: 1rem;
  overflow-y: auto;
  flex: 1;
}

.modal-footer-caja {
  padding: .75rem 1rem;
  border-top: 1px solid var(--border-main);
  flex-shrink: 0;
}

.btn-close-caja {
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  color: var(--text-muted);
  line-height: 1;
  padding: 0;
}
.btn-close-caja:hover { color: var(--accent-danger); }
</style>
